



















































































































































import Vue from 'vue';

import { iconsMixin } from '@/mixins/icons';
import { breakpointsMixin } from '@/mixins/breakpoints';

import { GeocodingRequest } from '@/models/location/GeocodingRequest';
import { ZipCode } from '@/models/zipCodes/ZipCode';
import DropdownOption from '@/models/misc/DropdownOption';

import zipCodesService from '@/services/ZipCodesService';
import countryService from '@/services/CountryService';
import locationsService from '@/services/LocationsService';
import mapsService from '@/services/MapsService';

export default Vue.extend<any, any, any, any>({
  props: ['zipCode', 'isEdit'],
  mixins: [iconsMixin, breakpointsMixin],
  data() {
    return {
      zipCodeData: {
        zipCode: '',
        city: '',
        state: '',
        stateAbbreviation: '',
        country: '',
        latitude: null,
        longitude: null,
      } as ZipCode,
      selectedCountry: null as DropdownOption | null,
      selectedState: null as DropdownOption | null,
      countriesDdl: [] as DropdownOption[],
      statesDdl: [] as DropdownOption[],
      errors: {} as any,
      saving: false,
      lookingUpCoordinates: false,
    };
  },
  created() {
    this.init();
    this.resetValidation();
  },
  mounted() {
    this.resetValidation();
    if (this.isEdit && this.zipCode) {
      this.$nextTick(() => {
        this.populateForm();
      });
    }
  },
  watch: {
    zipCode: {
      handler(newVal) {
        if (newVal && this.isEdit) {
          this.$nextTick(() => {
            this.populateForm();
          });
        } else {
          this.resetValidation();
        }
      },
      immediate: true,
    },
    selectedCountry: {
      handler(newVal) {
        if (newVal && this.errors.country) {
          delete this.errors.country;
        }
      },
    },
    'zipCodeData.zipCode': {
      handler(newVal) {
        if (newVal && newVal.trim() && this.errors.zipCode) {
          delete this.errors.zipCode;
        }
      },
    },
    'zipCodeData.city': {
      handler(newVal) {
        if (newVal && newVal.trim() && this.errors.city) {
          delete this.errors.city;
        }
      },
    },
    selectedState: {
      handler(newVal) {
        if (newVal && this.errors.state) {
          delete this.errors.state;
        }
      },
    },
    'zipCodeData.latitude': {
      handler(newVal) {
        if (this.errors.latitude) {
          // Clear error if field is empty or has valid number
          if (
            !newVal ||
            (!isNaN(parseFloat(newVal.toString())) &&
              parseFloat(newVal.toString()) >= -90 &&
              parseFloat(newVal.toString()) <= 90)
          ) {
            delete this.errors.latitude;
          }
        }
      },
    },
    'zipCodeData.longitude': {
      handler(newVal) {
        if (this.errors.longitude) {
          // Clear error if field is empty or has valid number
          if (
            !newVal ||
            (!isNaN(parseFloat(newVal.toString())) &&
              parseFloat(newVal.toString()) >= -180 &&
              parseFloat(newVal.toString()) <= 180)
          ) {
            delete this.errors.longitude;
          }
        }
      },
    },
  },
  methods: {
    resetValidation() {
      this.errors = {};
    },
    init() {
      this.countriesDdl = countryService.getAll();
    },
    populateForm() {
      if (!this.zipCode) return;

      // Copy the zip code data
      this.zipCodeData = { ...this.zipCode };

      // Ensure countries are loaded first
      if (this.countriesDdl.length === 0) {
        this.countriesDdl = countryService.getAll();
      }

      // Set selected country
      this.selectedCountry =
        this.countriesDdl.find((c) => c.value === this.zipCode.country) || null;

      if (this.selectedCountry) {
        // Load states for the country
        locationsService
          .getListOfStates(this.selectedCountry.value)
          .then((states) => {
            this.statesDdl = states.map(
              (state) => new DropdownOption(state.name, state.id)
            );

            // Set selected state - match by state abbreviation (which is stored as state.id)
            this.selectedState =
              this.statesDdl.find(
                (s) => s.value === this.zipCode.stateAbbreviation
              ) || null;
          })
          .catch((error) => {
            console.error('Error loading states:', error);
          });
      }
    },
    countryChanged(country: DropdownOption | null) {
      this.selectedCountry = country;
      this.selectedState = null;
      this.statesDdl = [];
      this.zipCodeData.country = country ? country.value : '';
      this.zipCodeData.state = '';
      this.zipCodeData.stateAbbreviation = '';

      if (country) {
        // Load states for selected country
        locationsService.getListOfStates(country.value).then((states) => {
          this.statesDdl = states.map(
            (state) => new DropdownOption(state.name, state.id)
          );
        });
      }
    },
    stateChanged(state: DropdownOption | null) {
      this.selectedState = state;
      if (state) {
        this.zipCodeData.state = state.text;
        this.zipCodeData.stateAbbreviation = state.value;
      } else {
        this.zipCodeData.state = '';
        this.zipCodeData.stateAbbreviation = '';
      }
    },
    lookupCoordinates() {
      if (
        !this.zipCodeData.zipCode ||
        !this.zipCodeData.city ||
        !this.selectedCountry
      ) {
        this.$toast.error(
          'Please fill in ZIP/Postal Code, City, and Country before looking up coordinates.'
        );
        return;
      }

      this.lookingUpCoordinates = true;

      const geocodingRequest = new GeocodingRequest(
        '', // address1 - not needed for our use case
        this.zipCodeData.city,
        this.zipCodeData.zipCode,
        this.zipCodeData.stateAbbreviation || '',
        this.selectedCountry.value
      );

      mapsService
        .getLatLongByAddress(geocodingRequest)
        .then((result) => {
          if (result && result.latitude && result.longitude) {
            this.zipCodeData.latitude = result.latitude;
            this.zipCodeData.longitude = result.longitude;
            this.$toast.show('Coordinates looked up successfully.');
          } else {
            this.$toast.error('Could not find coordinates for this location.');
          }
        })
        .catch((error) => {
          console.error('Error looking up coordinates:', error);
          this.$toast.error('Error looking up coordinates.');
        })
        .finally(() => {
          this.lookingUpCoordinates = false;
        });
    },
    validate() {
      this.errors = {};

      if (!this.selectedCountry || !this.zipCodeData.country) {
        this.errors.country = 'Country is required.';
      }

      if (!this.selectedState || !this.zipCodeData.stateAbbreviation) {
        this.errors.state = 'State is required.';
      }

      if (!this.zipCodeData.zipCode || !this.zipCodeData.zipCode.trim()) {
        this.errors.zipCode = 'ZIP/Postal Code is required.';
      }

      if (!this.zipCodeData.city || !this.zipCodeData.city.trim()) {
        this.errors.city = 'City is required.';
      }

      if (
        this.zipCodeData.latitude !== null &&
        this.zipCodeData.latitude !== undefined
      ) {
        const lat = parseFloat(this.zipCodeData.latitude.toString());
        if (isNaN(lat) || lat < -90 || lat > 90) {
          this.errors.latitude =
            'Latitude must be a valid number between -90 and 90.';
        }
      }

      if (
        this.zipCodeData.longitude !== null &&
        this.zipCodeData.longitude !== undefined
      ) {
        const lng = parseFloat(this.zipCodeData.longitude.toString());
        if (isNaN(lng) || lng < -180 || lng > 180) {
          this.errors.longitude =
            'Longitude must be a valid number between -180 and 180.';
        }
      }

      return Object.keys(this.errors).length === 0;
    },
    save() {
      if (!this.validate()) {
        return;
      }

      this.saving = true;

      const savePromise = this.isEdit
        ? zipCodesService.update(this.zipCodeData)
        : zipCodesService.create(this.zipCodeData);

      savePromise
        .then(() => {
          this.$emit('saved');
        })
        .catch(() => {
          this.$toast.error('Error saving ZIP/Postal Code.');
        })
        .finally(() => {
          this.saving = false;
        });
    },
    closeModal() {
      this.$emit('close');
    },
  },
});
